import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Send, Edit3, CheckCircle2, MessageSquareText,
  Clock, AlertTriangle, User, Users, Tag as TagIcon, ShieldCheck, PenLine, X, Plus, Hammer
} from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";
import FileUploader from "@/components/FileUploader";
import AddActionDialog from "@/components/AddActionDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { formatDate, formatDateTime, riskBand, FIVE_WHYS_THRESHOLD } from "@/lib/format";

export default function ActionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [comment, setComment] = useState("");
  const [commentPhotos, setCommentPhotos] = useState([]);
  const [commentSig, setCommentSig] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [whysOpen, setWhysOpen] = useState(false);
  const [discussOpen, setDiscussOpen] = useState(false);
  const [workReqOpen, setWorkReqOpen] = useState(false);
  const [relatedChat, setRelatedChat] = useState([]);

  const loadRelatedChat = async () => {
    try { const r = await api.get(`/records/action/${id}/related-chat`); setRelatedChat(r.data || []); }
    catch (e) { /* ignore */ }
  };

  const load = async () => {
    try {
      const r = await api.get(`/actions/${id}`);
      setA(r.data);
      setCommentSig(user?.name || "");
    } catch (e) {
      toast.error("Action not found or no access");
      nav("/app/actions");
    }
  };
  useEffect(() => { load(); loadRelatedChat(); /* eslint-disable-next-line */ }, [id]);

  // Deep-link support for sub-task anchors (e.g. from Calendar): scroll into view once data loads.
  useEffect(() => {
    if (!a) return;
    const hash = window.location.hash;
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
  }, [a]);

  if (!a) return <div className="p-10 font-mono text-[#1a1a1a]/60">Loading…</div>;

  const isLate = a.due_date && new Date(a.due_date) < new Date() && a.status !== "completed";
  const score = (a.likelihood && a.severity) ? a.likelihood * a.severity : (a.risk_score || null);
  const band = riskBand(score);
  const needs5W = score && score >= FIVE_WHYS_THRESHOLD;
  const has5W = a.five_whys && (a.five_whys.whys || []).filter((w) => (w || "").trim()).length === 5;

  const postComment = async () => {
    if (!comment.trim() && commentPhotos.length === 0) return;
    if (!commentSig.trim()) return toast.error("Typed signature required");
    try {
      await api.post(`/actions/${id}/comment`, { text: comment, photo_ids: commentPhotos, signature_name: commentSig });
      setComment(""); setCommentPhotos([]);
      load();
      toast.success("Comment posted & signed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const complete = async () => {
    if (needs5W && !has5W) {
      toast.error("5-Whys root cause is required to close a high/critical-risk action");
      setWhysOpen(true);
      return;
    }
    try {
      await api.post(`/actions/${id}/complete`);
      toast.success("Marked completed — effectiveness review scheduled in 30 days");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5">
      {/* Hero */}
      <section className="relative hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative">
          <button data-testid="action-back" onClick={() => nav(-1)} className="btn-back">
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <div className="mt-3 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {a.display_id && (
                  <span className="inline-flex items-center gap-2 bg-[#ff4500] text-white rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] font-bold" data-testid="action-display-id">
                    {a.display_id}
                  </span>
                )}
                <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Status · {a.status}</span>
                </span>
              </div>
              <h1 className="mt-2 font-display font-black text-2xl lg:text-3xl tracking-tight leading-tight" data-testid="action-title-display">{a.title}</h1>
              {a.description && <p className="mt-1 text-sm text-white/80 max-w-2xl">{a.description}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] font-mono uppercase tracking-wider text-white/70">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> Due {formatDate(a.due_date) || "—"} {isLate && <span className="text-[#ff5f15]"><AlertTriangle className="w-3 h-3 inline" /> overdue</span>}
                </span>
                <span className="inline-flex items-center gap-1.5">Created by {a.created_by_name}</span>
                <span className="inline-flex items-center gap-1.5">{formatDateTime(a.created_at)}</span>
                <span className="inline-flex items-center gap-1.5"><TagIcon className="w-3 h-3" /> {(a.tags || []).join(" · ") || "—"}</span>
                {score && band && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-bold text-white" style={{ background: band.color }} data-testid="action-risk-badge">
                    L{a.likelihood}×S{a.severity} = {score} · {band.label}
                  </span>
                )}
                {a.waste_category && (
                  <span className="inline-flex items-center gap-1.5 bg-white/10 px-2 py-0.5 rounded-full">
                    Waste · {a.waste_category}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="action-edit" onClick={() => setEditOpen(true)} className="btn-pill btn-pill-ghost">
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </button>
              {needs5W && (
                <button data-testid="action-five-whys" onClick={() => setWhysOpen(true)} className="btn-pill bg-white text-[#1a1a1a] hover:bg-[#ff4500] hover:text-white">
                  <ShieldCheck className="w-3.5 h-3.5" /> {has5W ? "5-Whys ✓" : "5-Whys required"}
                </button>
              )}
              {a.status !== "completed" && (
                <button data-testid="action-complete" onClick={complete} className="btn-pill btn-pill-primary"
                  disabled={needs5W && !has5W}
                  title={needs5W && !has5W ? "Complete 5-Whys first" : ""}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                </button>
              )}
              <button data-testid="action-open-trace" onClick={() => nav(`/app/trace/action/${a.id}`)}
                className="btn-pill btn-pill-secondary"
                title="Open the audit→action→chat chain (for auditors)">
                <ShieldCheck className="w-3.5 h-3.5" /> Trace
              </button>
              <button data-testid="action-discuss-in-hub" onClick={() => setDiscussOpen(true)}
                className="btn-pill btn-pill-secondary"
                title="Post a hub chat message that auto-links to this action + the audit that triggered it">
                <MessageSquareText className="w-3.5 h-3.5" /> Discuss in hub
              </button>
              <button data-testid="action-work-request" onClick={() => setWorkReqOpen(true)}
                className="btn-pill btn-pill-secondary"
                title="Email a work request to maintenance — they reply with a WO number that gets linked back">
                <Hammer className="w-3.5 h-3.5" /> Work request
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Audit source panel — shows the 3 boxes: Audit Question / Auditor's Response / Action Details */}
      {a.source_audit && <AuditSourcePanel src={a.source_audit} action={a} />}

      {/* 5-Whys panel (always shown when score >= threshold OR when already recorded) */}
      {(needs5W || a.five_whys) && (
        <FiveWhysPanel a={a} onEdit={() => setWhysOpen(true)} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Activity feed */}
        <div className="lg:col-span-2 surface-card rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-[#fff1ea] flex items-center justify-center"><MessageSquareText className="w-4 h-4 text-[#1a1a1a]" /></div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Audit-logged · every change signed</div>
              <div className="font-display font-bold text-base">Activity</div>
            </div>
          </div>

          <ol className="relative space-y-3 pl-4 border-l-2 border-[#fff1ea]">
            {(a.activity || []).map((e) => (
              <li key={e.id} className="relative" data-testid={`activity-${e.id}`}>
                <span className={`absolute -left-[1.4rem] top-1.5 w-3 h-3 rounded-full ${e.kind === "comment" ? "bg-[#ff4500]" : "bg-[#1a1a1a]"}`} />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold font-mono text-[#ff4500]">{e.kind}</span>
                  <span className="font-display font-bold text-sm">{e.user_name}</span>
                  <span className="text-[10px] font-mono text-[#1a1a1a]/55">{formatDateTime(e.created_at)}</span>
                </div>
                <div className="text-sm text-[#1a1a1a]/85 mt-0.5 whitespace-pre-wrap">{e.text}</div>
                {(e.photo_ids || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {e.photo_ids.map((pid) => (
                      <img key={pid} src={fileUrl(pid)} alt="" className="w-16 h-16 object-cover rounded-lg border border-[#1a1a1a]/10" />
                    ))}
                  </div>
                )}
              </li>
            ))}
            {(a.activity || []).length === 0 && (
              <li className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">No activity yet</li>
            )}
          </ol>

          {/* Compose */}
          <div className="mt-5 bg-[#f7f5f2] border border-[#1a1a1a]/08 rounded-xl p-3">
            <textarea data-testid="action-comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note, update, or evidence…"
              className="w-full bg-white border border-[#1a1a1a]/10 rounded-xl px-3 py-2 text-sm h-20 focus:outline-none focus:border-[#1a1a1a]" />
            <div className="mt-2 flex items-end justify-between gap-2 flex-wrap">
              <PhotoUploader photoIds={commentPhotos} onChange={setCommentPhotos} testIdPrefix="comment" />
            </div>
            <div className="mt-2 flex items-center gap-2 bg-[#1a1a1a] text-white rounded-xl px-3 py-2">
              <PenLine className="w-3.5 h-3.5 text-[#ff5f15]" />
              <input data-testid="action-comment-sig" value={commentSig} onChange={(e) => setCommentSig(e.target.value)}
                placeholder="Type your name to sign this comment…"
                className="flex-1 bg-transparent border-0 text-sm focus:outline-none font-display font-bold placeholder:text-white/40" />
              <button data-testid="action-post-comment" onClick={postComment} className="btn-pill btn-pill-primary">
                <Send className="w-3.5 h-3.5" /> Post & sign
              </button>
            </div>
          </div>
        </div>

        {/* Side: assignment + photos */}
        <div className="space-y-5">
          <div className="surface-card rounded-2xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// Assignment</div>
            <div className="space-y-2">
              <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/65 flex items-center gap-1.5"><User className="w-3 h-3" /> People</div>
              <div className="flex flex-wrap gap-1.5">
                {(a.assigned_users || []).map((u) => (
                  <span key={u.id} className="inline-flex items-center gap-1.5 bg-[#fff1ea] text-[#1a1a1a] text-[11px] font-bold rounded-full px-2.5 py-1">
                    <span className="w-5 h-5 rounded-full bg-[#1a1a1a] text-white text-[9px] flex items-center justify-center">{u.name?.[0]?.toUpperCase() || "?"}</span>
                    {u.name}
                  </span>
                ))}
                {(a.assigned_users || []).length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">—</span>}
              </div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/65 flex items-center gap-1.5 mt-3"><Users className="w-3 h-3" /> Hubs</div>
              <div className="flex flex-wrap gap-1.5">
                {(a.assigned_teams || []).map((t) => (
                  <span key={t.id} className="text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 bg-[#ff4500] text-white">{t.name}</span>
                ))}
                {(a.assigned_teams || []).length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">—</span>}
              </div>
            </div>
          </div>
          {(a.photo_ids || []).length > 0 && (
            <div className="surface-card rounded-2xl p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// Photos</div>
              <div className="grid grid-cols-3 gap-2">
                {a.photo_ids.map((pid) => (
                  <img key={pid} src={fileUrl(pid)} alt="" className="w-full aspect-square object-cover rounded-lg border border-[#1a1a1a]/10" />
                ))}
              </div>
            </div>
          )}
          {/* General file attachments — PDFs, work orders, docs */}
          <div className="surface-card rounded-2xl p-6" data-testid="action-files-section">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Files ({(a.file_ids || []).length})</div>
            </div>
            <ActionFileAttachments action={a} onChanged={load} />
          </div>

          {/* MS Planner-style sub-tasks */}
          <ActionSubTasks action={a} onChanged={load} />
        </div>
      </div>

      <AddActionDialog open={editOpen} onClose={() => setEditOpen(false)} initial={a} onCreated={load} />
      <FiveWhysDialog open={whysOpen} onClose={() => setWhysOpen(false)} a={a} onSaved={load} />
      <DiscussInHubDialog open={discussOpen} onClose={() => setDiscussOpen(false)} action={a}
        onPosted={() => loadRelatedChat()} />
      <WorkRequestDialog open={workReqOpen} onClose={() => setWorkReqOpen(false)} action={a} onSent={load} />

      {/* Related discussions — chat messages linked to this action (or to the audit that triggered it) */}
      <RelatedDiscussionsPanel rec={a} kind="action" relatedChat={relatedChat} />

      {/* Effectiveness Review — bottom of card */}
      {(a.status === "completed" || a.effectiveness_status) && (
        <EffectivenessReviewPanel a={a} onUpdated={load} />
      )}
    </div>
  );
}

function FiveWhysPanel({ a, onEdit }) {
  const has = a.five_whys && (a.five_whys.whys || []).filter((w) => (w || "").trim()).length === 5;
  return (
    <div className="surface-card rounded-2xl p-6" data-testid="five-whys-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-[#fff1ea] flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-[#ff4500]" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Root cause · 5-Whys</div>
            <div className="font-display font-bold text-base">Why did this happen?</div>
          </div>
        </div>
        <button data-testid="five-whys-edit" onClick={onEdit} className="btn-pill btn-pill-secondary">
          <Edit3 className="w-3.5 h-3.5" /> {has ? "Edit" : "Complete now"}
        </button>
      </div>
      {has ? (
        <ol className="space-y-2">
          {a.five_whys.whys.map((w, i) => (
            <li key={i} className="flex gap-3" data-testid={`why-${i}`}>
              <span className="w-7 h-7 rounded-full bg-[#1a1a1a] text-white font-display font-black text-xs flex items-center justify-center shrink-0">{i + 1}</span>
              <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">Why {i + 1}?</div>
                <div className="text-sm text-[#1a1a1a] mt-0.5">{w}</div>
              </div>
            </li>
          ))}
          <li className="text-[10px] font-mono uppercase tracking-wider text-[#1a8754] mt-2">
            ✓ Signed by {a.five_whys.signed_by} · {formatDateTime(a.five_whys.signed_at)}
          </li>
        </ol>
      ) : (
        <div className="py-6 text-center bg-[#fff1ea] border border-dashed border-[#ff4500]/30 rounded-xl">
          <div className="font-display font-bold text-base text-[#ff4500]">Required before closing</div>
          <div className="text-[11px] text-[#1a1a1a]/65 mt-1">High/critical-risk actions must capture their root cause via the 5-Whys method.</div>
        </div>
      )}
      {a.five_whys?.root_cause_summary && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white border-2 border-[#1a1a1a]/10 rounded-xl p-3" data-testid="whys-root-cause-display">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1a1a1a]/55">Root cause summary</div>
            <div className="text-sm font-display font-bold mt-1 whitespace-pre-wrap">{a.five_whys.root_cause_summary}</div>
          </div>
          {(a.five_whys.corrective_action || a.five_whys.preventive_action) ? (
            <>
              {a.five_whys.corrective_action && (
                <div className="bg-white border-2 border-[#ff4500]/30 rounded-xl p-3" data-testid="whys-ca-display">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">Corrective action · immediate fix</div>
                  <div className="text-sm font-display font-bold mt-1 whitespace-pre-wrap">{a.five_whys.corrective_action}</div>
                </div>
              )}
              {a.five_whys.preventive_action && (
                <div className="bg-white border-2 border-[#1a8754]/30 rounded-xl p-3" data-testid="whys-pa-display">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1a8754]">Preventive action · stops it happening again</div>
                  <div className="text-sm font-display font-bold mt-1 whitespace-pre-wrap">{a.five_whys.preventive_action}</div>
                </div>
              )}
            </>
          ) : a.five_whys.suggested_capa && (
            <div className="bg-white border-2 border-[#ff4500]/30 rounded-xl p-3" data-testid="whys-capa-display">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">Suggested CAPA</div>
              <div className="text-sm font-display font-bold mt-1 whitespace-pre-wrap">{a.five_whys.suggested_capa}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FiveWhysDialog({ open, onClose, a, onSaved }) {
  const { user } = useAuth();
  const [whys, setWhys] = useState(["", "", "", "", ""]);
  const [sig, setSig] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [correctiveAction, setCorrectiveAction] = useState("");
  const [preventiveAction, setPreventiveAction] = useState("");
  const [suggestedCapa, setSuggestedCapa] = useState(""); // kept for backend backward-compat
  const [busy, setBusy] = useState(false);
  const [spawnOpen, setSpawnOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const existing = a?.five_whys?.whys || [];
    setWhys([0,1,2,3,4].map((i) => existing[i] || ""));
    setSig(user?.name || "");
    setRootCause(a?.five_whys?.root_cause_summary || "");
    // Try to load CA/PA if backend stores them; otherwise split the legacy suggested_capa string
    const savedCa = a?.five_whys?.corrective_action || "";
    const savedPa = a?.five_whys?.preventive_action || "";
    const legacy = a?.five_whys?.suggested_capa || "";
    setCorrectiveAction(savedCa || legacy);
    setPreventiveAction(savedPa);
    setSuggestedCapa(legacy);
  }, [open, a, user]);

  const submit = async () => {
    if (whys.some((w) => !w.trim())) return toast.error("All 5 'why?' answers are required");
    if (!sig.trim()) return toast.error("Typed signature required");
    setBusy(true);
    try {
      await api.post(`/actions/${a.id}/five-whys`, { whys, signature_name: sig });
      // Persist root cause + CA + PA (backend still accepts suggested_capa for backward-compat;
      // we keep it as a summary "CA · PA" combined string so old readers still see something)
      if (rootCause.trim() || correctiveAction.trim() || preventiveAction.trim()) {
        const combined = [correctiveAction.trim() ? `CA: ${correctiveAction.trim()}` : "",
                          preventiveAction.trim() ? `PA: ${preventiveAction.trim()}` : ""].filter(Boolean).join("\n\n");
        await api.patch(`/actions/${a.id}/five-whys/extend`, {
          root_cause_summary: rootCause,
          suggested_capa: combined,
          corrective_action: correctiveAction,
          preventive_action: preventiveAction,
        });
      }
      toast.success("5-Whys signed and locked");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl rounded-2xl border-[#1a1a1a]/15 max-h-[90vh] overflow-auto scrollbar-thin">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#ff4500]" /> 5-Whys root cause
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-[12px] text-[#1a1a1a]/75">
              Start with the problem statement, then ask &quot;why?&quot; five times — each answer becomes the next prompt — to reach the true root cause.
            </div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
              Problem: <span className="text-[#1a1a1a]">{a?.title}</span>
            </div>
            {whys.map((w, i) => (
              <div key={i}>
                <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Why {i + 1}?</label>
                <input data-testid={`why-input-${i}`} value={w}
                  onChange={(e) => setWhys(whys.map((v, idx) => idx === i ? e.target.value : v))}
                  placeholder={i === 0 ? "Because…" : "Because of the previous answer…"}
                  className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a]" />
              </div>
            ))}
            {/* Extended: root-cause summary + CA + PA split (iter30) */}
            <div className="bg-[#fff1ea] border-2 border-[#ff4500]/30 rounded-xl p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// After the 5th why · summarise + propose CA and PA</div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Root cause summary</label>
              <textarea data-testid="whys-root-cause" value={rootCause}
                onChange={(e) => setRootCause(e.target.value)}
                placeholder="In plain English — what is the true root cause?"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm h-16" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                <div className="bg-white border border-[#ff4500]/25 rounded-lg p-2.5">
                  <label className="block text-[11px] uppercase tracking-[0.18em] text-[#ff4500] font-mono font-bold">Corrective Action (CA)</label>
                  <div className="text-[11px] text-[#1a1a1a]/70 mb-1.5 italic">What can be done to <strong>immediately fix</strong> the issue?</div>
                  <textarea data-testid="whys-corrective-action" value={correctiveAction}
                    onChange={(e) => setCorrectiveAction(e.target.value)}
                    placeholder="Immediate fix — e.g. Replace belt, re-train operator, contain suspect product…"
                    className="w-full bg-[#faf8f4] border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm h-20" />
                </div>
                <div className="bg-white border border-[#1a8754]/25 rounded-lg p-2.5">
                  <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a8754] font-mono font-bold">Preventive Action (PA)</label>
                  <div className="text-[11px] text-[#1a1a1a]/70 mb-1.5 italic">What can be done to <strong>keep it from happening again</strong>?</div>
                  <textarea data-testid="whys-preventive-action" value={preventiveAction}
                    onChange={(e) => setPreventiveAction(e.target.value)}
                    placeholder="Lasting change — e.g. Update SOP, add sensor, PM schedule, poka-yoke, training on new procedure…"
                    className="w-full bg-[#faf8f4] border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm h-20" />
                </div>
              </div>

              <button data-testid="whys-generate-action" type="button" onClick={() => setSpawnOpen(true)}
                disabled={!(correctiveAction.trim() || preventiveAction.trim())}
                className="btn-pill btn-pill-primary text-[11px] mt-3 disabled:opacity-50">
                <Plus className="w-3 h-3" /> Generate follow-on action(s) from this CA / PA
              </button>
              <div className="text-[10px] uppercase tracking-wider text-[#1a1a1a]/70 mt-1 font-mono">
                <strong className="text-[#ff4500]">This creates NEW action(s)</strong> based on the CA and PA above — one owner-assignable, due-dated action per box you filled in. Both link back to this parent so 3rd-party auditors can trace the full <em>5-Whys → CA/PA → follow-on</em> chain.
              </div>
            </div>
            <div className="bg-[#1a1a1a] text-white rounded-xl p-3 mt-3">
              <div className="flex items-center gap-2 mb-1.5">
                <PenLine className="w-3.5 h-3.5 text-[#ff5f15]" />
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff5f15]">// Signature · locks the record</div>
              </div>
              <input data-testid="five-whys-sig" value={sig} onChange={(e) => setSig(e.target.value)}
                placeholder="Type your full name…"
                className="w-full bg-white/10 border border-white/25 rounded-xl px-3 py-2 text-sm font-display font-bold focus:outline-none focus:border-white" />
              <div className="text-[10px] font-mono uppercase tracking-wider text-white/55 mt-1">
                {sig || "(unsigned)"} · {formatDateTime(new Date())}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="five-whys-submit" onClick={submit} disabled={busy} className="btn-pill btn-pill-primary">
              <ShieldCheck className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Sign & save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SpawnFromWhysDialog open={spawnOpen} onClose={() => setSpawnOpen(false)} parent={a}
        suggestedTitle={suggestedCapa.slice(0, 100)} suggestedDescription={suggestedCapa}
        onCreated={() => { toast.success("Follow-on action created and linked"); onSaved(); }} />
    </>
  );
}

function EffectivenessReviewPanel({ a, onUpdated }) {
  const due = a.effectiveness_review_at ? new Date(a.effectiveness_review_at) : null;
  const now = new Date();
  const isDue = due && due <= now && a.effectiveness_status === "pending";
  const daysLeft = due ? Math.ceil((due - now) / (1000 * 60 * 60 * 24)) : null;
  const [verifyOpen, setVerifyOpen] = useState(null); // "verified" | "not_verified"

  const banner = a.effectiveness_status === "verified" ? (
    <div className="bg-[#1a8754]/10 border border-[#1a8754]/30 text-[#1a8754] rounded-xl px-4 py-3 text-sm">
      <div className="font-display font-bold">✓ Verified effective</div>
      <div className="text-[11px] mt-1 text-[#1a1a1a]/70">
        Signed by {a.effectiveness_signature || a.effectiveness_verified_by} · {formatDateTime(a.effectiveness_verified_at)}
      </div>
      {a.effectiveness_notes && <div className="text-[12px] text-[#1a1a1a]/80 mt-1">&ldquo;{a.effectiveness_notes}&rdquo;</div>}
      {((a.effectiveness_photo_ids || []).length > 0 || (a.effectiveness_file_ids || []).length > 0) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(a.effectiveness_photo_ids || []).map((pid) => (
            <img key={pid} src={fileUrl(pid)} alt="" className="w-14 h-14 object-cover rounded-lg border border-[#1a1a1a]/10" />
          ))}
        </div>
      )}
    </div>
  ) : a.effectiveness_status === "not_verified" ? (
    <div className="bg-[#ff4500]/10 border border-[#ff4500]/30 text-[#ff4500] rounded-xl px-4 py-3 text-sm">
      <div className="font-display font-bold">✗ Not verified — action re-opened</div>
      <div className="text-[11px] mt-1 text-[#1a1a1a]/70">
        Signed by {a.effectiveness_signature || a.effectiveness_verified_by} · {formatDateTime(a.effectiveness_verified_at || a.updated_at)}
      </div>
      {a.effectiveness_notes && <div className="text-[12px] text-[#1a1a1a]/80 mt-1">&ldquo;{a.effectiveness_notes}&rdquo;</div>}
    </div>
  ) : null;

  return (
    <div className="surface-card rounded-2xl p-6 mt-5" data-testid="effectiveness-review-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-[#fff1ea] flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-[#ff4500]" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// 30-day effectiveness</div>
            <div className="font-display font-bold text-base">Effectiveness review</div>
          </div>
        </div>
        {due && (
          <span className={`iso-chip ${isDue ? "iso-chip-orange" : "iso-chip-carbon"}`}>
            {isDue ? "DUE NOW" : daysLeft >= 0 ? `IN ${daysLeft} DAY${daysLeft === 1 ? "" : "S"}` : "OVERDUE"}
            · {formatDate(due)}
          </span>
        )}
      </div>

      {banner}

      {!a.effectiveness_status || a.effectiveness_status === "pending" ? (
        <div className="mt-3 bg-[#f7f5f2] border border-[#1a1a1a]/08 rounded-xl p-3">
          <div className="text-[12px] text-[#1a1a1a]/80">
            30 days after completion, verify the corrective action <b>prevented recurrence</b>. A signed comment <b>and</b> proof attachment are required.
            If not verified, the action will re-open automatically.
          </div>
          {isDue && (
            <div className="mt-2 flex gap-2 flex-wrap">
              <button data-testid="effectiveness-verify" onClick={() => setVerifyOpen("verified")}
                className="btn-pill btn-pill-primary">✓ Verify — effective</button>
              <button data-testid="effectiveness-not-verified" onClick={() => setVerifyOpen("not_verified")}
                className="btn-pill btn-pill-secondary">✗ Not verified — re-open</button>
            </div>
          )}
        </div>
      ) : null}

      <EffectivenessVerifyDialog open={!!verifyOpen} verdict={verifyOpen} onClose={() => setVerifyOpen(null)}
        actionId={a.id} onSaved={onUpdated} />
    </div>
  );
}

function EffectivenessVerifyDialog({ open, verdict, onClose, actionId, onSaved }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setNotes(""); setPhotos([]); setSig(user?.name || ""); }
  }, [open, user]);

  const submit = async () => {
    if (!notes.trim()) return toast.error("Verification comment is required");
    if (photos.length === 0) return toast.error("At least one proof photo is required");
    if (!sig.trim()) return toast.error("Typed signature required");
    setBusy(true);
    try {
      await api.post(`/actions/${actionId}/verify-effectiveness`, {
        verdict, notes, photo_ids: photos, file_ids: [], signature_name: sig,
      });
      toast.success(verdict === "verified" ? "Signed off as effective" : "Re-opened — signed");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl rounded-2xl border-[#1a1a1a]/15 max-h-[90vh] overflow-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            {verdict === "verified" ? <CheckCircle2 className="w-5 h-5 text-[#1a8754]" /> : <X className="w-5 h-5 text-[#ff4500]" />}
            {verdict === "verified" ? "Verify effectiveness" : "Mark not-verified · re-open"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[12px] text-[#1a1a1a]/75">
            Provide a comment, at least one proof photo, and sign your name to record this verdict for compliance.
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Verification comment</label>
            <textarea data-testid="effectiveness-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={verdict === "verified" ? "Evidence of effectiveness — what observation confirms recurrence was prevented?" : "Why the corrective action did not prevent recurrence…"}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-20 focus:outline-none focus:border-[#1a1a1a]" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Proof attachments (photo required)</label>
            <PhotoUploader photoIds={photos} onChange={setPhotos} testIdPrefix="effectiveness" />
          </div>
          <div className="bg-[#1a1a1a] text-white rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <PenLine className="w-3.5 h-3.5 text-[#ff5f15]" />
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff5f15]">// Signature · audit trail</div>
            </div>
            <input data-testid="effectiveness-sig" value={sig} onChange={(e) => setSig(e.target.value)}
              placeholder="Type your full name…"
              className="w-full bg-white/10 border border-white/25 rounded-xl px-3 py-2 text-sm font-display font-bold focus:outline-none focus:border-white" />
            <div className="text-[10px] font-mono uppercase tracking-wider text-white/55 mt-1">
              {sig || "(unsigned)"} · {formatDateTime(new Date())}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="effectiveness-submit" onClick={submit} disabled={busy}
            className={`btn-pill ${verdict === "verified" ? "btn-pill-primary" : "btn-pill-secondary"}`}>
            {busy ? "Saving…" : verdict === "verified" ? "Sign & verify" : "Sign & re-open"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DiscussInHubDialog({ open, onClose, action, onPosted }) {
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/teams").then((r) => {
      setTeams(r.data || []);
      if (r.data?.length) setTeamId(r.data[0].id);
    }).catch(() => {});
    setMessage(`Discussion on ${action?.human_id || action?.display_id || "ACT"} — ${(action?.title || "").slice(0, 60)}\n\n`);
  }, [open, action]);
  const send = async () => {
    if (!teamId) return toast.error("Pick a hub");
    if (!message.trim()) return toast.error("Message required");
    setBusy(true);
    try {
      await api.post("/chat", {
        team_id: teamId, message: message.trim(),
        linked_kind: "action", linked_id: action.id,
      });
      toast.success("Posted to hub — auto-linked to this Action + the originating audit");
      onPosted?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl border-[#1a1a1a]/15">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            <MessageSquareText className="w-5 h-5 text-[#ff4500]" /> Discuss in hub
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[12px] text-[#1a1a1a]/65 bg-[#fff1ea] border border-[#ff4500]/15 rounded-xl p-3">
            Posting here auto-links the chat to <b>{action?.human_id || action?.display_id}</b>
            {action?.source_run_id && <> AND the audit run that triggered it</>}. Auditors will see the full chain on the Trace page.
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Post to which hub?</label>
            <select data-testid="discuss-team-select" value={teamId} onChange={(e) => setTeamId(e.target.value)}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
              {teams.length === 0 && <option value="">No hubs you belong to</option>}
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <textarea data-testid="discuss-message" value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="What's the situation?"
            className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-32" />
        </div>
        <DialogFooter>
          <button data-testid="discuss-submit" onClick={send} disabled={busy || !teamId} className="btn-pill btn-pill-primary disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> {busy ? "Posting…" : "Post & link to record"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RelatedDiscussionsPanel({ rec, kind, relatedChat }) {
  if (!relatedChat || relatedChat.length === 0) return null;
  return (
    <div className="surface-card rounded-2xl p-5 mt-4" data-testid="related-discussions-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Related discussions</div>
          <div className="font-display font-bold text-base">Hub chat linked to this {kind} ({relatedChat.length})</div>
        </div>
      </div>
      <ul className="divide-y divide-[#1a1a1a]/08">
        {relatedChat.slice(-10).reverse().map((m) => (
          <li key={m.id} className="py-2 text-[12px]" data-testid={`related-chat-${m.id}`}>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#ff4500] flex items-center gap-2 flex-wrap">
              <span className="font-bold">{m.user_name}</span>
              {m.user_title && <span className="text-[#1a1a1a]/55">· {m.user_title}</span>}
              <span className="text-[#1a1a1a]/55">· {new Date(m.created_at).toLocaleString()}</span>
              <span className="text-[#1a1a1a]/40">· signed {m.signature_name || m.user_name}</span>
            </div>
            <div className="leading-snug mt-0.5 whitespace-pre-wrap">{m.message}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}




function AuditSourcePanel({ src, action }) {
  return (
    <section className="surface-card rounded-2xl p-6" data-testid="audit-source-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Originating audit context</div>
          <div className="font-display font-bold text-base">Where this action came from</div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mt-0.5">
            {src.template_name} · {src.run_display_id} · closed by {src.closed_by || "—"}
          </div>
        </div>
        <a href={`/app/trace/action/${action.id}`}
          className="btn-pill btn-pill-secondary text-[10px]">
          <ShieldCheck className="w-3 h-3" /> Open full trace
        </a>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <BoxCard tone="#1a1a1a" title="1 · Audit question" body={src.question} sub={src.instructions} testId="src-question" />
        <BoxCard tone="#d6a900" title="2 · Auditor's response" body={src.response} sub={src.response_note ? `Note: ${src.response_note}` : ""} testId="src-response" />
        <BoxCard tone="#ff4500" title="3 · Action details entered" body={src.action_details_at_trigger || "—"} testId="src-action-details" />
      </div>
    </section>
  );
}

function BoxCard({ tone, title, body, sub, testId }) {
  return (
    <div data-testid={testId} className="bg-white border-2 rounded-xl p-3" style={{ borderColor: tone }}>
      <div className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: tone }}>{title}</div>
      <div className="text-sm font-display font-bold mt-1 whitespace-pre-wrap">{body || "—"}</div>
      {sub && <div className="text-[11px] text-[#1a1a1a]/65 mt-2 whitespace-pre-wrap">{sub}</div>}
    </div>
  );
}


function SpawnFromWhysDialog({ open, onClose, parent, suggestedTitle, suggestedDescription, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", likelihood: 3, severity: 3,
    due_date: "", assigned_user_ids: [], assigned_team_ids: [],
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm({
      title: suggestedTitle || `CAPA follow-on for ${parent?.display_id || ""}`,
      description: suggestedDescription || "",
      likelihood: 3, severity: 3,
      due_date: "", assigned_user_ids: [], assigned_team_ids: [],
    });
  }, [open, parent, suggestedTitle, suggestedDescription]);
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      await api.post(`/actions/${parent.id}/spawn-from-whys`, form);
      onCreated?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const rs = form.likelihood * form.severity;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl border-[#1a1a1a]/15 max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#ff4500]" /> Generate follow-on action
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[12px] text-[#1a1a1a]/70 bg-[#fff1ea] border border-[#ff4500]/20 rounded-xl p-2.5">
            This action will link back to <b>{parent?.display_id}</b> and inherit its audit source, so auditors can trace the full 5-Whys → CAPA → follow-on chain.
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Title</label>
            <input data-testid="spawn-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Description</label>
            <textarea data-testid="spawn-description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-20" />
          </div>
          <RiskEducation likelihood={form.likelihood} severity={form.severity}
            onChange={(L, S) => setForm({ ...form, likelihood: L, severity: S })} />
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Due date</label>
            <input type="date" data-testid="spawn-due" value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="spawn-submit" onClick={submit} disabled={busy || !form.title.trim()} className="btn-pill btn-pill-primary disabled:opacity-50">
            <Plus className="w-3.5 h-3.5" /> {busy ? "Creating…" : `Create · Risk ${rs}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RiskEducation({ likelihood, severity, onChange }) {
  const rs = likelihood * severity;
  const band = rs >= 16 ? { name: "Critical", color: "#ff4500" } :
               rs >= 12 ? { name: "High", color: "#d6a900" } :
               rs >= 6  ? { name: "Medium", color: "#1a1a1a" } :
                          { name: "Low", color: "#1a8754" };
  return (
    <div className="bg-white border-2 border-[#1a1a1a]/10 rounded-xl p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// Risk assessment · you decide</div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">
            Likelihood (L) — how often could this happen?
          </label>
          <select data-testid="risk-likelihood" value={likelihood}
            onChange={(e) => onChange(Number(e.target.value), severity)}
            className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm">
            <option value={1}>1 · Rare — once in years</option>
            <option value={2}>2 · Unlikely — a few times a year</option>
            <option value={3}>3 · Possible — monthly</option>
            <option value={4}>4 · Likely — weekly</option>
            <option value={5}>5 · Almost certain — daily</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">
            Severity (S) — how bad if it happens?
          </label>
          <select data-testid="risk-severity" value={severity}
            onChange={(e) => onChange(likelihood, Number(e.target.value))}
            className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm">
            <option value={1}>1 · Negligible — no real impact</option>
            <option value={2}>2 · Minor — small disruption</option>
            <option value={3}>3 · Moderate — customer / SOP impact</option>
            <option value={4}>4 · Major — line down, injury risk</option>
            <option value={5}>5 · Catastrophic — recall / harm</option>
          </select>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between bg-[#f7f5f2] rounded-lg px-3 py-1.5">
        <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
          L{likelihood} × S{severity} = Risk score
        </div>
        <div className="font-display font-black text-xl" style={{ color: band.color }}>
          {rs} · {band.name}
        </div>
      </div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/45 mt-1">
        High or Critical (12+) will require a 5-Whys before this action can be closed.
      </div>
    </div>
  );
}



function WorkRequestDialog({ open, onClose, action, onSent }) {
  const [existing, setExisting] = useState([]);
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setEmail(""); setSubject(""); setBody("");
    api.get(`/actions/${action.id}/work-requests`).then((r) => setExisting(r.data || [])).catch(() => {});
  }, [open, action?.id]);
  const send = async () => {
    if (!email) return toast.error("Maintenance email required");
    setBusy(true);
    try {
      await api.post(`/actions/${action.id}/work-request`, {
        to_email: email, subject: subject || undefined, body: body || undefined,
      });
      toast.success("Work request emailed (MOCKED). Maintenance will reply with a Work Order number.");
      onSent?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const assignWO = async (wrId, val) => {
    if (!val.trim()) return;
    try {
      await api.patch(`/work-requests/${wrId}`, { work_order_number: val.trim() });
      toast.success("Work Order # linked");
      const r = await api.get(`/actions/${action.id}/work-requests`);
      setExisting(r.data || []);
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl border-[#1a1a1a]/15 max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            <Hammer className="w-5 h-5 text-[#ff4500]" /> Send work request to maintenance
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {existing.length > 0 && (
            <div className="space-y-1" data-testid="wr-existing-list">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Already sent</div>
              {existing.map((wr) => (
                <div key={wr.id} className="bg-[#f7f5f2] rounded-md px-2 py-1.5 text-[12px] flex items-center gap-2 flex-wrap">
                  <span className="font-mono">{wr.to_email}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#1a1a1a]/55">{new Date(wr.created_at).toLocaleDateString()}</span>
                  <span className="ml-auto flex items-center gap-1">
                    {wr.work_order_number ? (
                      <span className="text-[10px] uppercase tracking-wider font-bold rounded-full bg-[#e8f5ee] text-[#1a8754] px-2 py-0.5">WO # {wr.work_order_number}</span>
                    ) : (
                      <input placeholder="WO #" data-testid={`wr-wo-${wr.id}`}
                        onBlur={(e) => assignWO(wr.id, e.target.value)}
                        className="w-24 bg-white border border-[#1a1a1a]/15 rounded px-1.5 py-0.5 text-[11px]" />
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Maintenance email</label>
            <input type="email" data-testid="wr-email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="maintenance@plant.com"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Subject (optional)</label>
            <input value={subject} data-testid="wr-subject" onChange={(e) => setSubject(e.target.value)}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Body (optional — smart default sent otherwise)</label>
            <textarea value={body} data-testid="wr-body" onChange={(e) => setBody(e.target.value)}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-20" />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="wr-send" onClick={send} disabled={busy || !email} className="btn-pill btn-pill-primary disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send work request"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function ActionFileAttachments({ action, onChanged }) {
  const [ids, setIds] = useState(action.file_ids || []);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/actions/${action.id}`, { file_ids: ids });
      toast.success(`Saved ${ids.length} file attachment${ids.length === 1 ? "" : "s"}`);
      setDirty(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };
  const update = (next) => { setIds(next); setDirty(true); };
  return (
    <div>
      <div className="text-[11px] text-[#1a1a1a]/65 mb-2">
        Attach completed work orders, signed inspection reports, purchase orders, calibration certificates — any evidence auditors need. Photos live in their own section above.
      </div>
      <FileUploader fileIds={ids} onChange={update} testIdPrefix="action-files" />
      {dirty && (
        <div className="mt-3 flex gap-2">
          <button data-testid="action-files-save" onClick={save} disabled={busy}
                  className="btn-pill btn-pill-primary text-[11px]">
            {busy ? "Saving…" : "Save attachments"}
          </button>
          <button data-testid="action-files-cancel" onClick={() => { setIds(action.file_ids || []); setDirty(false); }}
                  className="btn-pill btn-pill-ghost text-[11px]">Cancel</button>
        </div>
      )}
    </div>
  );
}

/** Sub-tasks (MS Planner-style) on an action.
 *  Each sub-task has title, description, assignees, due date (<= parent action due), files, status.
 *  Action is only fully "done" when all sub-tasks are completed.
 */
function ActionSubTasks({ action, onChanged }) {
  const [subs, setSubs] = useState(action.sub_tasks || []);
  const [showNew, setShowNew] = useState(false);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSubs(action.sub_tasks || []); }, [action.sub_tasks]);
  useEffect(() => { api.get("/users/visible-to-me").then((r) => setUsers(r.data || [])).catch(() => {}); }, []);

  const done = subs.filter((s) => s.status === "completed").length;
  const pct = subs.length ? Math.round((done / subs.length) * 100) : 0;

  const add = async (payload) => {
    setBusy(true);
    try {
      await api.post(`/actions/${action.id}/subtasks`, payload);
      toast.success("Sub-task added");
      setShowNew(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const patch = async (stid, patchBody) => {
    try {
      await api.patch(`/actions/${action.id}/subtasks/${stid}`, patchBody);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const remove = async (stid) => {
    if (!window.confirm("Delete this sub-task?")) return;
    await api.delete(`/actions/${action.id}/subtasks/${stid}`);
    onChanged?.();
  };

  return (
    <div className="surface-card rounded-2xl p-6" data-testid="action-subtasks-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Sub-tasks ({done}/{subs.length})</div>
          <div className="font-display font-bold text-base">Break the work into steps</div>
        </div>
        <button data-testid="subtask-add-toggle" onClick={() => setShowNew((v) => !v)} className="btn-pill btn-pill-primary text-[11px]">
          + Add sub-task
        </button>
      </div>
      {subs.length > 0 && (
        <div className="mb-3">
          <div className="h-2 bg-[#f7f5f2] rounded-full overflow-hidden">
            <div className="h-full bg-[#1a8754] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mt-1">{pct}% complete</div>
        </div>
      )}
      {showNew && <SubTaskCreator users={users} maxDue={action.due_date} onCreate={add} onCancel={() => setShowNew(false)} busy={busy} />}
      {subs.length === 0 && !showNew && (
        <div className="text-[11px] text-[#1a1a1a]/55 py-4 text-center">
          No sub-tasks yet. Add the discrete steps needed (e.g. &quot;Get quote&quot;, &quot;Purchase belt&quot;, &quot;Install guard&quot;) — each with its own owner and due date.
        </div>
      )}
      <ul className="space-y-2" data-testid="subtask-list">
        {subs.map((s) => <SubTaskRow key={s.id} st={s} users={users} onPatch={patch} onRemove={remove} maxDue={action.due_date} />)}
      </ul>
    </div>
  );
}

function SubTaskCreator({ users, maxDue, onCreate, onCancel, busy }) {
  const [form, setForm] = useState({ title: "", description: "", assigned_user_ids: [], due_date: "", priority: "normal" });
  const toggle = (uid) => setForm({ ...form, assigned_user_ids: form.assigned_user_ids.includes(uid)
    ? form.assigned_user_ids.filter((x) => x !== uid) : [...form.assigned_user_ids, uid] });
  const canSave = form.title.trim().length > 0;
  return (
    <div className="bg-[#faf8f4] border border-[#1a1a1a]/10 rounded-xl p-3 space-y-2 mb-3" data-testid="subtask-creator">
      <input data-testid="subtask-title" value={form.title} maxLength={120} onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Step title (e.g. Get quote for belt and purchase)"
        className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm font-display font-bold" />
      <textarea data-testid="subtask-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Details (optional)" className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-xs h-12" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mb-1">Due date {maxDue && <span className="text-[#ff4500]">(≤ {maxDue})</span>}</label>
          <input data-testid="subtask-due" type="date" value={form.due_date} max={maxDue || undefined}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mb-1">Priority</label>
          <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-sm">
            {["low", "normal", "high", "critical"].map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mb-1">Assignees</label>
        <div className="flex flex-wrap gap-1">
          {users.map((u) => {
            const on = form.assigned_user_ids.includes(u.id);
            return (
              <button key={u.id} type="button" onClick={() => toggle(u.id)}
                data-testid={`subtask-assign-${u.id}`}
                className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${on ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20"}`}>
                {u.name}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex gap-2">
        <button data-testid="subtask-save" disabled={!canSave || busy} onClick={() => onCreate(form)}
          className="btn-pill btn-pill-primary text-[11px] disabled:opacity-50">
          {busy ? "Saving…" : "Add sub-task"}
        </button>
        <button data-testid="subtask-cancel" onClick={onCancel} className="btn-pill btn-pill-ghost text-[11px]">Cancel</button>
      </div>
    </div>
  );
}

function SubTaskRow({ st, users, onPatch, onRemove, maxDue }) {
  const done = st.status === "completed";
  const [expanded, setExpanded] = useState(false);
  const assigneeNames = (st.assigned_user_ids || []).map((id) => users.find((u) => u.id === id)?.name).filter(Boolean).join(", ");
  const overdue = !done && st.due_date && new Date(st.due_date) < new Date();
  return (
    <li id={`subtask-${st.id}`} className={`border rounded-xl overflow-hidden scroll-mt-24 ${done ? "bg-[#e8f5ee] border-[#1a8754]/30" : overdue ? "bg-[#fff1ea] border-[#ff4500]/30" : "bg-white border-[#1a1a1a]/10"}`} data-testid={`subtask-${st.id}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <input type="checkbox" checked={done} data-testid={`subtask-${st.id}-toggle`}
          onChange={(e) => onPatch(st.id, { status: e.target.checked ? "completed" : "open" })}
          className="w-4 h-4 accent-[#1a8754] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className={`font-display font-bold text-sm truncate ${done ? "line-through text-[#1a1a1a]/60" : ""}`}>{st.title}</div>
          <div className="text-[10px] font-mono text-[#1a1a1a]/55 flex items-center gap-2 flex-wrap">
            {st.due_date && <span className={overdue ? "text-[#ff4500] font-bold" : ""}>due {st.due_date}</span>}
            {assigneeNames && <span>· {assigneeNames}</span>}
            {st.priority && st.priority !== "normal" && <span>· {st.priority}</span>}
            {done && st.completed_by_name && <span>· done by {st.completed_by_name}</span>}
          </div>
        </div>
        <button onClick={() => setExpanded((v) => !v)} className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 hover:text-[#ff4500]" data-testid={`subtask-${st.id}-expand`}>
          {expanded ? "less" : "edit"}
        </button>
        <button onClick={() => onRemove(st.id)} data-testid={`subtask-${st.id}-remove`} className="text-[#ff4500]/60 hover:text-[#ff4500] text-xs">✕</button>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-[#1a1a1a]/06 space-y-2 bg-[#faf8f4]">
          {st.description && <div className="text-[12px] text-[#1a1a1a]/75 whitespace-pre-wrap">{st.description}</div>}
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">// Attachments</div>
          <FileUploader fileIds={st.file_ids || []}
            onChange={(ids) => onPatch(st.id, { file_ids: ids })}
            testIdPrefix={`subtask-${st.id}-file`} />
          <div className="flex items-center gap-2 mt-1">
            <input type="date" defaultValue={st.due_date || ""} max={maxDue || undefined}
              onChange={(e) => onPatch(st.id, { due_date: e.target.value || null })}
              data-testid={`subtask-${st.id}-due-edit`}
              className="bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-xs" />
            <span className="text-[10px] font-mono text-[#1a1a1a]/55">edit due · must be ≤ parent action</span>
          </div>
        </div>
      )}
    </li>
  );
}

